import React, { useState, useEffect } from "react";
import { useGlobal } from "../context/ContenedorGlobal";
import "../styles/Formularios.css";
import "../styles/CrearSolicitudes.css";
import InputSelectGenerico from "./InputSelectGenerico";
import InputSelectTecPorCat from "./InputSelectTecPorCat";
import InputSelectFallas from "./InputSelectFallas";
import InputGenerico from "./InputGenerico";
import { useApiFetch } from "../utils/apiFetch";
import InputUsuarioAutocomplete from "./InputUsuarioAutocomplete";

function CrearSolicitudes() {
    const { setModuloActual, setSubModuloActual, logout } = useGlobal();
    const [idUsuario, setIdUsuario] = useState("");
    const [solicitanteEsOperador, setSolicitanteEsOperador] = useState(false);
    const [idUso, setIdUso] = useState("");
    const [bienesObtenidos, setBienesObtenidos] = useState([]);
    const [bienSeleccionado, setBienSeleccionado] = useState(null);
    const [idCategoria, setIdCategoria] = useState("");
    const [idTecnico, setIdTecnico] = useState("");
    const [idFalla, setIdFalla] = useState(null);
    const [notas, setNotas] = useState("");
    const [paso, setPaso] = useState(1);
    const apiFetch = useApiFetch();
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);
    const [mensaje, setMensaje] = useState("");

    // Función auxiliar para mostrar y limpiar los mensajes
    const mostrarError = (mensaje) => {
        setError(mensaje);
        setTimeout(() => setError(""), 2000);
    };

    // Función para registrar la solicitud en la tabla solicitudes
    const handleCrearSolicitud = async (e) => {
        e.preventDefault();

        // limpiar estados previos
        setError("");
        setSuccess("");

        // Validaciones
        if (!idUsuario) {
            mostrarError("Debe seleccionar un solicitante.");
            return;
        }

        if (!idUso) {
            mostrarError("Debe seleccionar un operador.");
            return;
        }

        if (!bienSeleccionado) {
            mostrarError("Debe seleccionar un bien.");
            return;
        }

        if (!idCategoria) {
            mostrarError("Debe seleccionar una categoría.");
            return;
        }

        if (!idTecnico) {
            mostrarError("Debe seleccionar un técnico.");
            return;
        }

        if (!idFalla) {
            mostrarError("Debe seleccionar una falla.");
            return;
        }

        const notasTrim = (notas ?? "").trim();
        if (notasTrim.length === 0) {
            mostrarError("Debe ingresar notas de la solicitud.");
            return;
        }

        if (notasTrim.length > 255) {
            mostrarError("Las notas no deben exceder 255 caracteres.");
            return;
        }

        setLoading(true);

        const payload = {
            id_usuario: idUsuario,
            id_uso: idUso,
            serie_bien: bienSeleccionado,
            id_categoria: idCategoria,
            id_tecnico: idTecnico,
            id_falla: idFalla,
            notas: notas
        };

        try {
            const data = await apiFetch("crear_solicitud.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload)
            }, logout);

            if (data.status === "ok") {
                setSuccess(`Solicitud creada con éxito. Folio: ${data.folio}`);
            } else {
                setError(data.message || "Error al registrar la solicitud.");
            }
        } catch (error) {
            setError("Error al conectar con el servidor: " + error.message);
        } finally {
            setLoading(false);
            setTimeout(() => {
                setModuloActual("Solicitudes");
                setSubModuloActual(null);
            }, 3000);
        }
    };

    // Función para buscar los bienes asignados al usuario operador idUso
    const handleBuscarBienesAsignados = async (e) => {
        e.preventDefault();
        setError("");
        setSuccess("");
        setLoading(true);

        try {

            const data = await apiFetch("consultar_bienes_operador.php", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ id_uso: idUso })
            }, logout);

            if (data.status === "ok") {
                setBienesObtenidos(data.resultado || []);
                setPaso(2);
                setSuccess("Bienes encontrados.");
                setTimeout(() => {
                    setSuccess("");
                }, 1000);
            } else {
                setError(data.message || "Error en la consulta.");
                setTimeout(() => {
                    setModuloActual("Solicitudes");
                    setSubModuloActual(null);
                    setLoading(false);
                }, 3000);
            }
        } catch (error) {
            setError("Error al conectar con el servidor.");
            setTimeout(() => {
                setModuloActual("Solicitudes");
                setSubModuloActual(null);
                setLoading(false);
            }, 3000);
        } finally {
            setLoading(false);
        }
    };

    // Efecto sesundario para actualizae el estado del id del usuario operador idUso, en caso de que el solicitante tambien sea el operador
    useEffect(() => {
        if (solicitanteEsOperador) {
            setIdUso(idUsuario);
        }
    }, [solicitanteEsOperador, idUsuario]);

    // Efecto secundario para cuando cambia idUso, buscar bienes automáticamente
    useEffect(() => {
        if (idUso) {
            handleBuscarBienesAsignados({ preventDefault: () => { } });
        } else {
            setBienesObtenidos([]);
            setBienSeleccionado("");
        }
    }, [idUso]);

    // Mostrar formulario al usuario
    return (
        <div className="sesion-form">
            {error && <div className="error">{error}</div>}
            {success && <div className="success">{success}</div>}

            <form onSubmit={handleCrearSolicitud} className="form-grid">
                {/* Columna 1: Solicitante */}
                <div className="columna">
                    <InputUsuarioAutocomplete
                        idSeleccionado={idUsuario}
                        setIdSeleccionado={setIdUsuario}
                        label="Solicitante"
                        placeholder="Ingrese apellido paterno"
                        title="Seleccione al usuario que reporta la solicitud"
                        readOnly={false}
                        maxLength={50}
                    />

                    <div className="form-group">
                        <label>
                            <input
                                type="checkbox"
                                checked={solicitanteEsOperador}
                                onChange={(e) => setSolicitanteEsOperador(e.target.checked)}
                            />
                            El que reporta opera el equipo
                        </label>
                    </div>

                    {!solicitanteEsOperador && (
                        < InputUsuarioAutocomplete
                            idSeleccionado={idUso}
                            setIdSeleccionado={setIdUso}
                            label="Operador"
                            placeholder="Ingrese apellido paterno"
                            title="Seleccione al usuario operador"
                            readOnly={false}
                            maxLength={50}
                        />
                    )}
                </div>

                {/* Columna 2: Bienes y Categoría */}
                <div className="columna">
                    {bienesObtenidos.length > 0 && (
                        <div className="tabla-estandar seleccion-unica">
                            <h3>Seleccione el bien</h3>
                            <table>
                                <thead>
                                    <tr>
                                        <th>Seleccionar</th>
                                        <th>Tipo</th>
                                        <th>Serie</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {bienesObtenidos.map((bien) => (
                                        <tr key={bien.serie_bien}>
                                            <td>
                                                <input
                                                    type="radio"
                                                    name="bienSeleccionado"
                                                    value={bien.serie_bien}
                                                    checked={bienSeleccionado === bien.serie_bien}
                                                    onChange={() => setBienSeleccionado(bien.serie_bien)}
                                                />
                                            </td>
                                            <td>{bien.tipo_bien}</td>
                                            <td>{bien.serie_bien}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <InputSelectGenerico
                        idSeleccionado={idCategoria}
                        setIdSeleccionado={setIdCategoria}
                        label="Seleccione la categoría"
                        apiUrl={`consultar_categorias.php`}
                        valueField="id_categoria"
                        displayField="categoria"
                        showDefaultOption={true}
                        defaultOptionText="Seleccione una categoría"
                    />
                </div>

                {/* Columna 3: Servicio */}
                <div className="columna">
                    <InputSelectTecPorCat
                        idCategoria={idCategoria}
                        idSeleccionado={idTecnico}
                        setIdSeleccionado={setIdTecnico}
                    />

                    <InputSelectFallas
                        idCategoria={idCategoria}
                        idSeleccionado={idFalla}
                        setIdSeleccionado={setIdFalla}
                    />

                    <InputGenerico
                        value={notas}
                        setValue={setNotas}
                        label="Notas / solución"
                        maxLength={255}
                        allowedChars="A-Za-z0-9._\/ ñÑáéíóú@"
                        placeholder="Escriba su texto aquí"
                        title="Sea preciso con sus notas"
                    />

                    <div className="form-buttons">
                        {idUsuario && idUso && bienSeleccionado && idCategoria && idTecnico && idFalla && (
                            <button type="submit" disabled={loading}>
                                {loading ? "Creando..." : "Crear Solicitud"}
                            </button>
                        )}
                    </div>

                </div>

            </form>

        </div>
    );
}

export default CrearSolicitudes;